/**
 * Bridges the position service to the pure Recorder (spec 03): feeds fixes,
 * flushes a liveTrack for crash safety, and persists finalized tracks to IDB.
 */
import {
  Recorder,
  encodeTrack,
  decodeTrack,
  autoTrackName,
  thumbnailPath,
  type RecorderEvent,
  type FinalizedTrack,
  type TrackPoint,
} from '@boatnav/core';
import { posStore, type Fix } from '../nav/posStore.js';
import { recorderStore } from './recorderStore.js';
import { useOnboardingStore } from '../onboarding/onboardingStore.js';
import { uuid } from '../../lib/uuid.js';
import {
  saveFinalizedTrack,
  saveLiveTrack,
  getLiveTrack,
  clearLiveTrack,
  type TrackMeta,
} from '../../lib/db.js';

const FLUSH_INTERVAL_MS = 60_000;
const FLUSH_POINTS = 100;
const STALE_LIVE_MS = 30 * 60_000;

const recorder = new Recorder();
let trackId: string | null = null;
let lastFlush = 0;
let pointsSinceFlush = 0;
let started = false;
let unsub: (() => void) | null = null;

function toBuffer(points: TrackPoint[]): ArrayBuffer {
  const u8 = encodeTrack(points);
  const out = new ArrayBuffer(u8.byteLength);
  new Uint8Array(out).set(u8);
  return out;
}

async function flushLive(): Promise<void> {
  const snap = recorder.snapshot();
  if (!snap || !trackId) return;
  await saveLiveTrack({
    trackId,
    startedAt: snap.startedAt,
    buf: toBuffer(snap.points),
    pointCount: snap.pointCount,
    updatedAt: Date.now(),
  });
  lastFlush = Date.now();
  pointsSinceFlush = 0;
}

async function finalize(track: FinalizedTrack | null): Promise<void> {
  const id = trackId;
  recorderStore.getState().setRecording(false);
  recorderStore.getState().setTripDistance(0);
  trackId = null;
  await clearLiveTrack();

  if (track && id) {
    const meta: TrackMeta = {
      id,
      name: autoTrackName(track.startedAt),
      startedAt: track.startedAt,
      endedAt: track.endedAt,
      distanceNm: track.distanceNm,
      durationS: track.durationS,
      avgKn: track.avgKn,
      maxKn: track.maxKn,
      pointCount: track.pointCount,
      thumb: thumbnailPath(track.points),
      updatedAt: Date.now(),
      deletedAt: null,
    };
    await saveFinalizedTrack(meta, toBuffer(track.points));
  }
}

function handleEvents(events: RecorderEvent[]): void {
  for (const ev of events) {
    switch (ev.type) {
      case 'started':
        trackId = uuid();
        lastFlush = Date.now();
        pointsSinceFlush = 0;
        recorderStore.getState().setRecording(true);
        // First recording → show the boat-use setup guide (Phase 2).
        useOnboardingStore.getState().maybeShowSetup();
        break;
      case 'point':
        pointsSinceFlush += 1;
        break;
      case 'gap':
        recorderStore.getState().setGap(ev.at);
        // A visibility gap usually means the screen slept — re-surface the guide.
        useOnboardingStore.getState().maybeShowSetup(true);
        break;
      case 'finalized':
        void finalize(ev.track);
        break;
    }
  }
}

function onFix(fix: Fix): void {
  const events = recorder.push({
    lat: fix.lat,
    lon: fix.lon,
    sogKn: fix.sogKn,
    cogDeg: fix.cogDeg,
    ts: fix.ts,
  });
  handleEvents(events);

  if (recorder.isRecording) {
    recorderStore.getState().setTripDistance(recorder.tripDistanceNm);
    const now = Date.now();
    if (now - lastFlush >= FLUSH_INTERVAL_MS || pointsSinceFlush >= FLUSH_POINTS) {
      void flushLive();
    }
  }
}

/** Recover a leftover liveTrack: resume if fresh, auto-finalize if stale. */
async function recoverLiveTrack(): Promise<void> {
  const live = await getLiveTrack();
  if (!live) return;
  const points = decodeTrack(new Uint8Array(live.buf));
  recorder.resume({ startedAt: live.startedAt, points });
  trackId = live.trackId;

  if (Date.now() - live.updatedAt > STALE_LIVE_MS) {
    const events = recorder.stop();
    handleEvents(events);
    recorderStore.getState().setAutoEnded('Turen ble avsluttet automatisk.');
  } else {
    recorderStore.getState().setRecording(true);
    recorderStore.getState().setTripDistance(recorder.tripDistanceNm);
    lastFlush = Date.now();
  }
}

export function startRecorderService(): void {
  if (started) return;
  started = true;
  void recoverLiveTrack();
  unsub = posStore.subscribe((s, prev) => {
    if (s.fix !== prev.fix && s.fix) onFix(s.fix);
  });
}

export function stopRecorderService(): void {
  unsub?.();
  unsub = null;
  started = false;
}

/** Manual stop from the LOGGER chip confirm sheet. */
export function manualStop(): void {
  handleEvents(recorder.stop());
}
